import { BookOpen, Download, ExternalLink } from "lucide-react";

const EBOOK_URL = "https://drive.google.com/drive/folders/1hXAP2KZM3lGY0q69sZcRFA4nH0CvYdDs?usp=drive_link";

/**
 * Card that promotes and links to the members-only ebook on Google Drive.
 *
 * IMPLEMENTATION NOTE — we tried three approaches to bypass the
 * ERR_BLOCKED_BY_RESPONSE that shows when the destination is opened
 * inside a same-context tab (Emergent preview iframe + some Cloudflare
 * COEP/COOP combinations):
 *
 *   1. Plain <a target="_blank">                           → blocked
 *   2. <button onClick={window.open(...,'noopener')}>       → returned null, fallback fired
 *   3. Detached-anchor .click() via document.createElement  → works in Playwright but some
 *      user browsers still opened in-place
 *
 * Winning approach (this one): render a REAL <a> element with
 *   • href set to the Drive URL
 *   • target="_blank"          — request a new browsing context
 *   • rel="noopener noreferrer external"  — force a top-level, isolated context
 *   • an onClick handler that additionally calls `window.open(...)` WITHOUT any
 *     "noopener" feature. That means the anchor's native behavior handles the
 *     new-tab case, and if the browser's default navigation is intercepted (some
 *     iframe contexts), the JS window.open kicks in as a backup — with no null
 *     return value to trip up a fallback.
 * We deliberately do NOT set window.top.location, do NOT call preventDefault, and do
 * NOT rely on the return value of window.open.
 */
export default function EbookDownloadCard({ variant = "default" }) {
  const compact = variant === "compact";

  const belAndSuspenders = (e) => {
    // Let the anchor's native target="_blank" behavior take precedence. As a
    // silent redundancy, also try a scripted window.open (no `noopener` feature —
    // we still want the new tab to succeed even if the anchor click was intercepted).
    try {
      window.open(EBOOK_URL, "_blank");
    } catch (_) {
      /* ignore — anchor default nav will handle it */
    }
    // NOTE: we DO NOT call e.preventDefault() — we want the anchor's native
    // navigation to run as well.
    void e;
  };

  return (
    <div
      className={`rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent ${compact ? "p-4" : "p-6"}`}
      data-testid="ebook-card"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`shrink-0 ${compact ? "w-10 h-10" : "w-12 h-12"} rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center`}>
          <BookOpen className="text-amber-400" size={compact ? 18 : 22} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className={`font-display font-bold ${compact ? "text-base" : "text-xl"}`}>
            E-book Bongkar Teknik Designer Hasilkan Poster AI
          </div>
          <p className="text-sm text-neutral-400 mt-1">
            Panduan eksklusif untuk ahli — teknik, workflow &amp; rahsia designer profesional dalam menghasilkan poster berkualiti tinggi menggunakan AI.
          </p>
        </div>
        <a
          href={EBOOK_URL}
          target="_blank"
          rel="noopener noreferrer external"
          onClick={belAndSuspenders}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors text-sm no-underline"
          data-testid="ebook-download-btn"
        >
          <Download size={14} /> Download E-book <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}
